import { ToasterAction } from '@cycle-app/ui';
import { OfflineIcon } from '@cycle-app/ui/icons';
import { HocuspocusProvider } from '@hocuspocus/provider';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Doc } from 'yjs';

import { getDoc, setDoc } from 'src/reactives';
import { RouteParams } from 'src/types/routes.types';
import { addToaster, closeToaster } from 'src/utils/toasters.utils';

const PROVIDER_TOASTER_ID = 'provider-toaster';

export interface RealtimeEditorConfig {
  yDoc: Doc | null;
  provider: HocuspocusProvider | null;
  isSync: boolean;
  isOffline: boolean;
}

interface Params {
  docId: string | undefined | null;
  onInit?: (provider: HocuspocusProvider) => void;
  onSync?: (syncStatus: boolean) => void;
  isEnabled?: boolean;
  isEditorInit?: boolean;
  disableEditorOnDisconnect?: boolean;
}

export default function useRealtimeEditorConfig({
  docId,
  onInit,
  onSync,
  isEnabled = true,
  isEditorInit = false,
  disableEditorOnDisconnect = false,
}: Params): RealtimeEditorConfig {
  const [isOffline, setIsOffline] = useState(false);
  /**
   * The eslint disable is needed to let us ass the `docId` value which is
   * necessary when the doc change so we need to re-open sockets
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const collaborativeDoc = useMemo(() => (isEnabled ? new Doc() : null), [isEnabled, docId]);
  const [isSyncState, setIsSyncState] = useState(false);
  const previousClientID = useRef<number | null>(null);
  const routeParams = useParams<RouteParams>();

  const yprovider = useMemo(() => {
    if (
      !import.meta.env.VITE_WS_API_SYNC_SERVER ||
      !docId ||
      !isEnabled ||
      !collaborativeDoc
    ) {
      return null;
    }

    const provider = new HocuspocusProvider({
      url: `${import.meta.env.VITE_WS_API_SYNC_SERVER}/sync/doc/${docId}`,
      name: docId,
      document: collaborativeDoc,
      token: JSON.parse(localStorage.getItem('auth') || '{}').token,
      preserveConnection: false,
      forceSyncInterval: 5000,
      onDisconnect: () => {
        if (!isEnabled || !disableEditorOnDisconnect) return;
        setIsOffline(true);
        addToaster({
          id: PROVIDER_TOASTER_ID,
          icon: <OfflineIcon size={20} />,
          title: 'Editor disconnected',
          message: 'Please wait for it to reconnect',
          duration: 'infinite',
          closable: false,
          actions: (
            <ToasterAction onClick={() => provider.connect()}>
              Try to reconnect now
            </ToasterAction>
          ),
        });
      },
      onConnect: () => {
        if (!isEnabled || !disableEditorOnDisconnect) return;
        setIsOffline(false);
        closeToaster(PROVIDER_TOASTER_ID);
      },
      onDestroy: () => {
        if (!isEnabled || !disableEditorOnDisconnect) return;
        closeToaster(PROVIDER_TOASTER_ID);
      },
    });

    // Simulate disconnect in the console
    // > provider.disconnect()
    // > provider.connect()
    // (window as any).provider = provider;

    return provider;
  }, [docId, isEnabled, collaborativeDoc, disableEditorOnDisconnect]);

  useEffect(() => {
    setDoc({ yDoc: collaborativeDoc });
  }, [collaborativeDoc]);

  const isSync = previousClientID.current === collaborativeDoc?.clientID
    ? isSyncState
    : false;

  useEffect(() => {
    if (isEditorInit) {
      const {
        docId: id, isEditorSyncing,
      } = getDoc();

      if (!id || isEditorSyncing === !isSync) return;

      setDoc({ isEditorSyncing: !isSync });
    }
  }, [isSync, isEditorInit]);

  useEffect(() => {
    if (yprovider && collaborativeDoc) {
      setIsSyncState(yprovider.synced);
      previousClientID.current = collaborativeDoc.clientID;

      // yprovider.on('status', (event: any) => {
      //   console.info(`Socket ${docId} - ClientId ${collaborativeDoc.clientID} - ${event.status}`);
      // });

      yprovider.on('sync', (status: boolean) => {
        setIsSyncState(status);
      });
    }

    return () => {
      if (yprovider && collaborativeDoc) {
        yprovider.destroy();
      }
    };
  }, [yprovider, onInit, onSync, setIsSyncState, collaborativeDoc, docId]);

  useEffect(() => {
    if (routeParams.docSlug && isEditorInit) {
      const { isEditorSyncing } = getDoc();
      if (isEditorSyncing === !isSync) return;
      setDoc({ isEditorSyncing: !isSync });
    }
  }, [isSync, isEditorInit]);

  return {
    yDoc: collaborativeDoc,
    provider: yprovider,
    isSync,
    isOffline,
  };
}
